/**
 * POS Deduction Workflow Simulator
 * Simulates POS-triggered inventory deductions using the real PosDeduction API endpoint.
 * Tests all response scenarios: Completed (auto-deduct), EmailSent (manual action required).
 */

import { getSharedRateLimiter } from '../services/RateLimiter';
import { VirtualOperator } from '../services/VirtualOperator';
import { AuthService } from '../../../../../services/authService';
import { SimulatorConfig } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  return AuthService.authenticatedFetch(url, options);
};

export interface PosDeductionWorkflowResult {
  success: boolean;
  deductionsCompleted: number;
  deductionsAutoCompleted: number;
  deductionsEmailSent: number;
  error?: string;
  duration: number;
}

interface ItemBinInfo {
  itemNo: string;
  binCode: string;
  quantity: number;
  quantityAvailable: number;
  warehouseID: string;
}

interface PosDeductionResponse {
  status: string; // "Completed" | "EmailSent"
  itemNo: string;
  quantity: number;
  warehouseId: string;
  binCode?: string;
  previousQuantity?: number;
  newQuantity?: number;
  stcTransId?: number;
  completedAt?: string;
  reason?: string; // "NoLocationsFound" | "MultipleLocations" | "InsufficientQuantity"
  availableQuantity?: number;
  availableLocations?: Array<{ binCode: string; quantity: number }>;
  emailSentTo?: string[];
  emailSentAt?: string;
  message: string;
}

export class PosDeductionWorkflow {
  private config: SimulatorConfig;
  private operator: VirtualOperator;
  private rateLimiter = getSharedRateLimiter();

  constructor(config: SimulatorConfig, operator: VirtualOperator) {
    this.config = config;
    this.operator = operator;
  }

  async execute(): Promise<PosDeductionWorkflowResult> {
    const startTime = Date.now();
    let deductionsCompleted = 0;
    let deductionsAutoCompleted = 0;
    let deductionsEmailSent = 0;

    try {
      // Step 1: Get items with stock for deduction
      this.operator.startTask('posdeduction', 'Henter varer med lagerbeholdning');
      await this.rateLimiter.throttle();

      const itemBins = await this.getItemsWithStock();

      if (itemBins.length === 0) {
        return {
          success: true,
          deductionsCompleted: 0,
          deductionsAutoCompleted: 0,
          deductionsEmailSent: 0,
          duration: Date.now() - startTime,
          error: 'Ingen varer med lagerbeholdning funnet'
        };
      }

      // Step 2: Execute POS deductions
      const maxDeductions = this.config.workflows.posdeduction?.maxDeductions || 10;
      const maxQty = this.config.workflows.posdeduction?.maxQuantityPerDeduction || 5;
      const includeOverQtyTests = this.config.workflows.posdeduction?.includeOverQuantityTests ?? true;

      // Shuffle and limit items
      const shuffled = [...itemBins].sort(() => Math.random() - 0.5);
      const itemsToProcess = shuffled.slice(0, Math.min(maxDeductions, shuffled.length));

      for (const item of itemsToProcess) {
        // Determine quantity to request
        let requestQty: number;
        const shouldTestOverQuantity = includeOverQtyTests && Math.random() < 0.2; // 20% chance

        if (shouldTestOverQuantity) {
          // Request more than available to test InsufficientQuantity scenario
          requestQty = Math.ceil(item.quantityAvailable) + Math.floor(Math.random() * 5) + 1;
        } else {
          // Request a reasonable amount
          requestQty = Math.max(1, Math.min(
            Math.floor(Math.random() * maxQty) + 1,
            Math.floor(item.quantityAvailable)
          ));
        }

        if (requestQty <= 0) requestQty = 1;

        const externalRef = `SIM-POS-${this.operator.getId()}-${Date.now()}`;

        this.operator.startTask(
          'posdeduction',
          `POS fradrag: ${item.itemNo} x${requestQty} fra ${item.warehouseID}`
        );

        // Simulate scan/processing time
        await this.delay(this.config.timing.scanTimeMs);

        // Execute POS deduction
        await this.rateLimiter.throttle();
        const response = await this.executePosDeduction({
          itemNo: item.itemNo,
          quantity: requestQty,
          warehouseId: item.warehouseID,
          externalRef,
          description: `Simulator POS-salg ${new Date().toLocaleTimeString('no-NO')}`
        });

        deductionsCompleted++;

        if (response.status === 'Completed') {
          deductionsAutoCompleted++;
          console.log(
            `[PosDeductionWorkflow] Auto-deducted: ${item.itemNo} x${requestQty} from ${response.binCode}` +
            ` (${response.previousQuantity} → ${response.newQuantity})`
          );
        } else if (response.status === 'EmailSent') {
          deductionsEmailSent++;
          console.log(
            `[PosDeductionWorkflow] Email sent for: ${item.itemNo} x${requestQty}` +
            ` reason=${response.reason}, message=${response.message}`
          );
        }

        // Simulate walk time between deductions
        await this.delay(this.config.timing.walkTimeMs * 0.5); // Half walk time for POS
      }

      return {
        success: true,
        deductionsCompleted,
        deductionsAutoCompleted,
        deductionsEmailSent,
        duration: Date.now() - startTime
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ukjent feil';
      console.error(`[PosDeductionWorkflow] Error:`, errorMessage);

      return {
        success: false,
        deductionsCompleted,
        deductionsAutoCompleted,
        deductionsEmailSent,
        duration: Date.now() - startTime,
        error: errorMessage
      };
    }
  }

  /**
   * Get items with stock in the configured warehouse
   */
  private async getItemsWithStock(): Promise<ItemBinInfo[]> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/data-inspection/itembins`
      );

      if (response.ok) {
        const allBins: ItemBinInfo[] = await response.json();
        return allBins.filter((b) =>
          (b.warehouseID === this.config.warehouseId || (b as any).warehouse === this.config.warehouseId) &&
          (b.quantityAvailable || b.quantity) > 0
        );
      }
    } catch (error) {
      console.warn('[PosDeductionWorkflow] Could not get item bins:', error);
    }

    return [];
  }

  /**
   * Execute a POS deduction via the API
   */
  private async executePosDeduction(request: {
    itemNo: string;
    quantity: number;
    warehouseId: string;
    externalRef?: string;
    description?: string;
  }): Promise<PosDeductionResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/${this.config.companyNo}/pos-deduction`,
      {
        method: 'POST',
        body: JSON.stringify({
          ItemNo: request.itemNo,
          Quantity: request.quantity,
          WarehouseId: request.warehouseId,
          ExternalRef: request.externalRef,
          Description: request.description
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || error.message || `POS deduction failed: ${response.statusText}`);
    }

    return response.json();
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default PosDeductionWorkflow;
